// inlineBilder.ts — T-236: Bilder, die im Mailtext eingebettet sind (`cid:`),
// als Dateien behandeln. Reine Helfer, DOM-frei bis auf `dataUriZuBlob`.
//
// Überall sonst werden Inline-Anhänge weggefiltert (Signatur-Logos). Hier ist
// der eine Ort, der sie gezielt heraussucht — getrennt von den Datei-Anhängen,
// damit „Alle beim Kontakt speichern" und das Weiterleiten sie nie mitnehmen.

import { cidSchluessel, type MailAttachment } from "../../services/graphMailService";

/**
 * Unterhalb dieser Größe ist ein eingebettetes Bild fast immer ein Signatur-Logo
 * oder Social-Icon. Für die Übernahme in einen Vorgang wird es nicht angeboten;
 * anklicken und speichern lässt es sich trotzdem.
 */
export const MINDESTGROESSE_BILD = 5 * 1024;

/** Nur eingebettete Bilder mit Content-Id, ohne Dubletten. */
export function eingebetteteBilder(liste: MailAttachment[]): MailAttachment[] {
    const gesehen = new Set<string>();
    return liste.filter((a) => {
        if (!a.isInline || !a.contentId || !/^image\//i.test(a.contentType ?? "")) return false;
        const key = `${a.messageId}::${a.id}`;
        if (gesehen.has(key)) return false;
        gesehen.add(key);
        return true;
    });
}

/** Eingebettete Bilder, die für die Übernahme in einen Vorgang in Frage kommen (ohne Logos). */
export function bilderFuerVorgang(liste: MailAttachment[]): MailAttachment[] {
    return eingebetteteBilder(liste).filter((a) => (a.size ?? 0) >= MINDESTGROESSE_BILD);
}

/**
 * Den Anhang zu einem Bild im Mailtext finden. `messageId` bevorzugt die
 * Nachricht, in deren Text geklickt wurde — in einem Verlauf können zwei
 * Nachrichten dieselbe Content-Id tragen.
 */
export function findeBildZurCid(
    liste: MailAttachment[],
    cid: string | null,
    messageId?: string | null,
): MailAttachment | null {
    if (!cid) return null;
    const key = cidSchluessel(cid);
    const treffer = eingebetteteBilder(liste).filter((a) => cidSchluessel(a.contentId ?? "") === key);
    return treffer.find((a) => a.messageId === messageId) ?? treffer[0] ?? null;
}

const ENDUNG: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/gif": "gif",
    "image/webp": "webp",
    "image/bmp": "bmp",
    "image/svg+xml": "svg",
};

/** `image001.png`, `image.jpg`, `~WRD0001.jpg`, leer — Namen, die nichts sagen. */
const NICHTSSAGEND = /^(?:image|img|bild|inline|unnamed|attachment|~wrd)[\s_-]*\d*(?:\.[a-z0-9]+)?$/i;

/**
 * Dateiname für ein Bild aus dem Mailtext: ein sprechender Name bleibt, ein
 * Allerweltsname wird zu „Bild aus Mail 2026-10-07.png" — in der Akte und im
 * Download-Ordner sagt `image001.png` niemandem etwas.
 */
export function bildDateiname(name: string | null | undefined, contentType: string | null | undefined, datum: Date): string {
    const roh = (name ?? "").trim();
    if (roh && !NICHTSSAGEND.test(roh)) return roh;
    const ausName = /\.([a-z0-9]{2,5})$/i.exec(roh)?.[1]?.toLowerCase();
    const endung = ENDUNG[(contentType ?? "").toLowerCase().split(";")[0].trim()] ?? ausName ?? "png";
    const tag = Number.isNaN(datum.getTime()) ? "" : ` ${datum.toISOString().slice(0, 10)}`;
    return `Bild aus Mail${tag}.${endung}`;
}

/** MIME-Typ einer `data:`-URI (`null` bei `blob:` oder Unlesbarem). */
export function dataUriTyp(uri: string): string | null {
    return /^data:([^;,]+)[;,]/i.exec(uri)?.[1]?.toLowerCase() ?? null;
}

/** Blob aus der Quelle des Bilds — ohne Netzabruf bei `data:`, per `fetch` bei `blob:`. */
export async function dataUriZuBlob(uri: string): Promise<Blob> {
    const m = /^data:([^;,]*)((?:;[^;,]*)*),(.*)$/s.exec(uri);
    if (!m) return (await fetch(uri)).blob();
    const typ = m[1] || "application/octet-stream";
    if (/;base64/i.test(m[2])) {
        const bin = atob(m[3].replace(/\s+/g, ""));
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return new Blob([bytes], { type: typ });
    }
    return new Blob([decodeURIComponent(m[3])], { type: typ });
}
